
import { computed, defineComponent, PropType } from 'vue';
import useTraffic, { trafficOf } from './useTraffic';
import { StatusItem, TrafficUsage } from '@/types';

const safeNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const byteConvert = (data: number): string => {
  if (data < 1024) return `${data.toFixed(0)}B`;
  if (data < 1024 * 1024) return `${(data / 1024).toFixed(0)}K`;
  if (data < 1024 * 1024 * 1024) return `${(data / 1024 / 1024).toFixed(1)}M`;
  if (data < 1024 * 1024 * 1024 * 1024) return `${(data / 1024 / 1024 / 1024).toFixed(2)}G`;
  return `${(data / 1024 / 1024 / 1024 / 1024).toFixed(2)}T`;
};

export default defineComponent({
  name: 'DashboardOverview',
  props: {
    servers: {
      type: Array as PropType<Array<StatusItem>>,
      default: () => ([])
    }
  },
  setup(props) {
    const safeServers = computed(() => props.servers || []);
    const isOnline = (server: StatusItem): boolean => server.online4 || server.online6;
    const onlineServers = computed(() => safeServers.value.filter(isOnline));
    const totalCount = computed(() => safeServers.value.length);
    const onlineCount = computed(() => onlineServers.value.length);
    const offlineCount = computed(() => Math.max(totalCount.value - onlineCount.value, 0));
    const availabilityPercent = computed(() =>
      totalCount.value ? Math.round((onlineCount.value / totalCount.value) * 100) : 0
    );
    const totalRx = computed(() =>
      onlineServers.value.reduce((sum, server) => sum + safeNumber(server.network_rx), 0)
    );
    const totalTx = computed(() =>
      onlineServers.value.reduce((sum, server) => sum + safeNumber(server.network_tx), 0)
    );
    const totalRate = computed(() => totalRx.value + totalTx.value);
    const totalTraffic = computed(() =>
      safeServers.value.reduce((sum, server) =>
        sum + safeNumber(server.network_in) + safeNumber(server.network_out), 0)
    );
    const totalTrafficLimit = computed(() =>
      safeServers.value.reduce((sum, server) => sum + safeNumber(server.traffic_limit), 0)
    );
    const trafficPercent = computed(() =>
      totalTrafficLimit.value
        ? Math.min(100, Math.round((totalTraffic.value / totalTrafficLimit.value) * 100))
        : 0
    );
    const averageCpu = computed(() => {
      if (!onlineServers.value.length) return 0;
      const total = onlineServers.value.reduce((sum, server) => sum + safeNumber(server.cpu), 0);
      return Math.round(total / onlineServers.value.length);
    });
    const topLoadServer = computed(() =>
      onlineServers.value.reduce<StatusItem | undefined>((top, server) => {
        if (!top) return server;
        return safeNumber(server.load) > safeNumber(top.load) ? server : top;
      }, undefined)
    );
    const topLoadValue = computed(() =>
      topLoadServer.value ? safeNumber(topLoadServer.value.load).toFixed(1) : '0.0'
    );
    const topLoadName = computed(() => topLoadServer.value?.name || '暂无在线节点');
    const cpuPercent = (server: StatusItem): number =>
      Math.min(100, Math.max(0, safeNumber(server.cpu)));

    const { trafficEnabled } = useTraffic();
    const monthlyUsages = computed(() =>
      safeServers.value.map(server => trafficOf(server)).filter(usage => usage.tracked)
    );
    // 离线节点计数不可信，用量不计入合计，但额度仍然计入
    const onlineUsage = (usage: TrafficUsage): boolean => usage.state !== 'offline';
    const monthlyUsed = computed(() => monthlyUsages.value
      .filter(onlineUsage)
      .reduce((sum, usage) => sum + usage.used, 0)
    );
    // 百分比只对有额度的机器有意义，不能把不限额的用量掺进去
    const limitedUsages = computed(() => monthlyUsages.value.filter(usage => usage.limited));
    const monthlyLimit = computed(() =>
      limitedUsages.value.reduce((sum, usage) => sum + usage.limit, 0)
    );
    const monthlyLimitedUsed = computed(() => limitedUsages.value
      .filter(onlineUsage)
      .reduce((sum, usage) => sum + usage.used, 0)
    );
    const monthlyPercent = computed(() =>
      monthlyLimit.value ? Math.round((monthlyLimitedUsed.value / monthlyLimit.value) * 100) : 0
    );
    const monthlyLevel = computed(() =>
      monthlyPercent.value >= 100 ? 'error' : monthlyPercent.value >= 80 ? 'warning' : 'success'
    );
    const monthlyBarWidth = computed(() => Math.min(100, Math.max(0, monthlyPercent.value)));

    return {
      trafficEnabled,
      monthlyBarWidth,
      monthlyUsed,
      monthlyLimit,
      monthlyLimitedUsed,
      monthlyPercent,
      monthlyLevel,
      safeServers,
      totalCount,
      onlineCount,
      offlineCount,
      availabilityPercent,
      totalRx,
      totalTx,
      totalRate,
      totalTraffic,
      totalTrafficLimit,
      trafficPercent,
      averageCpu,
      topLoadValue,
      topLoadName,
      byteConvert,
      isOnline,
      cpuPercent
    };
  }
});
